import { CommonModule } from '@angular/common';
import {Component, Injector, OnDestroy, OnInit} from '@angular/core';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { Subject, forkJoin, takeUntil } from 'rxjs';
import { AuthService } from 'src/app/services/auth.service';
import {ApiService, CustomHttpParams} from 'src/app/services/api.service';
import {extractErrorMessage} from "../../../common/common";
import {LoaderComponent} from "../../common/loader/loader.component";
import {MetadataReportViewModel} from "../../../model/metadataReports.model";

interface Granularity {
  granularity: string | null;
  label: string | null;
  id: number | null;
  iso_8601_granularity: string;
}

@Component({
  selector: 'app-generate-update-report',
  standalone: true,
  imports: [RouterModule, CommonModule, FormsModule, ReactiveFormsModule, LoaderComponent],
  templateUrl: './generate-update-report.component.html',
  styleUrl: './generate-update-report.component.scss'
})
export class GenerateUpdateReportComponent implements OnInit, OnDestroy {
  protected isLoading: boolean = false;
  protected requestForm = new FormGroup({
    report_back_duration: new FormControl<string>('', [Validators.required]),
  });
  protected unsubscribe = new Subject<void>();
  protected formsSubscribe: any;
  protected metadataReports: MetadataReportViewModel[] = [];
  protected granularities: Granularity[] = [];

  public result: string = "No results yet.";
  public error: string | null = null;
  public backfill: boolean = false;
  public report_back_duration = '';
  public id: string = '';
  public granularitySelected: Granularity = {
    id: null,
    label: null,
    granularity: null,
    iso_8601_granularity: 'PT5M'
  }; // default value

  constructor(private injector: Injector, private authService: AuthService, private updateReportService: ApiService) {}

  ngOnInit(): void {
    this.isLoading = true;
    const queryParams: CustomHttpParams = {};
    forkJoin([
      this.updateReportService.getMetadataReports(queryParams).pipe(takeUntil(this.unsubscribe)),
      this.updateReportService.granularities().pipe(takeUntil(this.unsubscribe)),
    ]).subscribe({
      next: ([reports, granularities]: [MetadataReportViewModel[], Granularity[]]) => {
        if(reports) {
          this.metadataReports = reports;
        }
        if(granularities) {
          this.granularities = granularities;

          // Set default granularity
          if (this.granularities.length > 0) {
            this.granularitySelected = this.granularities[0];
          }
        }
        this.isLoading = false;
      },
      error: (err) => {
        console.error(err);
        this.error = extractErrorMessage(err,"Failed to load metadata reports or granularities.");
        this.isLoading = false;
      }
    });

    this.formsSubscribe = this.requestForm.controls.report_back_duration.valueChanges.pipe(takeUntil(this.unsubscribe)).subscribe(
      (val: any) => {
        this.report_back_duration = val;
      }
    );
  }

  setBackfill(event: any) {
    this.backfill = event.checked;
  }

  setGranularity(event: any) {
    this.granularitySelected = this.granularities.find((item) => item.iso_8601_granularity === event.value)!;
  }

  setChangeReport(event: any) {
    this.id = event.value;
  }

  performAction() {
    if (!this.id) {
      this.error = "Please select a report specifier.";
      return;
    }

    if (!this.report_back_duration) {
      this.error = "Report back duration is required.";
      return;
    }

    this.error = null;

    const obj: any = {
      granularity: this.granularitySelected.iso_8601_granularity,
      backfill: this.backfill,
      report_back_duration: this.report_back_duration,
      report_specifier_id: this.id
    };

    this.isLoading = true;
    this.updateReportService.updateReportMessage(obj).subscribe({
      next: (response: any) => {
        // Format XML for display
        try {
          this.result = new DOMParser()
            .parseFromString(response, "text/xml")
            .documentElement.outerHTML
            .replace(/></g, ">\n<")
            .replace(/><\/(\w+)/g, ">\n</\$1")
            .replace(/<(\w+)>/g, "<\$1>\n")
            .replace(/\n{2,}/g, "\n");
        } catch (e) {
          this.result = response;
        }
        this.isLoading = false;
      },
      error: (err: any) => {
        console.error(err);
        this.error = extractErrorMessage(err, "An error occurred while generating the update report message.");
        this.isLoading = false;
      }
    });
  }

  ngOnDestroy() {
    this.unsubscribe.next();
    this.unsubscribe.complete();
  }
}
